import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Form,
  Input,
  InputNumber,
  Table,
  DateRangePicker,
  AutoComplete,
  Radio,
} from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import { uniqueId } from 'lodash';
import useTable from '@/hooks/useTable';
import { getMessageTraceList, getAllTopics } from '@/services/utilService';
import ShadowCard from '@/components/ShadowCard';
import { isVoid, renderMillDateTime } from '@/utils/common';
import PopupLink from '@/components/Cluster/PopupLink';
import BuriedWrapper from '@/components/BuriedWrapper';
import Message from './Message';

const { FormItem } = Form;

function toDateString(djs) {
  if (djs) {
    return dayjs(djs).format();
  }
  return undefined;
}

function MessageList() {
  const [searchParams] = useSearchParams();
  const clusterName = searchParams.get('name');
  const [topicOptions, setTopicOptions] = useState([]);

  const loadOptions = useMemoizedFn(async () => {
    const resp = await getAllTopics({
      clusterName,
    });
    setTopicOptions(resp.data);
  });

  const presets = {
    最近3天: [dayjs().subtract(3, 'day'), dayjs()],
    最近24小时: [dayjs().subtract(1, 'day'), dayjs()],
    最近6小时: [dayjs().subtract(6, 'hour'), dayjs()],
    '2小时内': [dayjs().subtract(2, 'hour'), dayjs()],
  };

  const columns = [
    {
      colKey: 'messageId',
      title: '消息ID',
      width: 160,
      cell: ({ row }) => <Message row={row} clusterName={clusterName} />,
    },
    {
      colKey: 'messageKey',
      title: '消息Key',
      ellipsis: true,
      width: 150,
    },
    {
      colKey: 'producerName',
      title: '生产者名称',
      width: 200,
      ellipsis: true,
    },
    {
      colKey: 'clientAddress',
      title: '生产者地址',
      width: 200,
      ellipsis: true,
    },
    {
      colKey: 'topic',
      title: 'Topic',
      cell: ({ row }) => (
        <BuriedWrapper eventId='MESSAGE_TRACE_DETAIL_TRACE_TOPIC'>
          <PopupLink
            tip='跳转Topic'
            onClick={() => {
              window.open(`/cluster/topic/list?name=${clusterName}&topic=${row.topic}`);
            }}
          >
            {row.topic}
          </PopupLink>
        </BuriedWrapper>
      ),
    },
    {
      colKey: 'timestamp',
      title: '生产时间',
      sorter: true,
      cell: renderMillDateTime,
      width: 240,
    },
  ];

  const loadData = useMemoizedFn(async (params = {}) => {
    const startTime = toDateString(params.rangeTime[0]);
    const endTime = toDateString(params.rangeTime[1]);

    const reqParams = {
      ...params,
      startTime,
      endTime,
      cluster: clusterName,
    };

    if (isVoid(params.limit)) {
      reqParams.limit = 100;
    }

    delete reqParams.rangeTimeType;
    delete reqParams.rangeTime;

    const { data = [] } = await getMessageTraceList(reqParams);

    setData(data.map((item) => ({ ...item, key: uniqueId() })));
  });

  const onChangeTime = (value) => {
    switch (value) {
      case 'last-two-hour':
        form.setFieldsValue({ rangeTime: [dayjs().subtract(2, 'hour'), dayjs()] });
        break;
      case 'last-six-hour':
        form.setFieldsValue({ rangeTime: [dayjs().subtract(6, 'hour'), dayjs()] });
        break;
      case 'last-day':
        form.setFieldsValue({ rangeTime: [dayjs().subtract(1, 'day'), dayjs()] });
        break;
      case 'last-three-day':
        form.setFieldsValue({ rangeTime: [dayjs().subtract(3, 'day'), dayjs()] });
        break;
      default:
        break;
    }
  };

  const { header, config, setData, form, doSearch } = useTable({
    columns,
    onSearch: loadData,
    defaultSort: [{ descending: true, sortBy: 'timestamp' }],
    search: (
      <>
        <FormItem name='rangeTimeType' initialData='last-two-hour'>
          <Radio.Group onChange={onChangeTime}>
            <Radio.Button value='last-two-hour'>近2小时</Radio.Button>
            <Radio.Button value='last-six-hour'>近6小时</Radio.Button>
            <Radio.Button value='last-day'>近24小时</Radio.Button>
            <Radio.Button value='last-three-day'>近3天</Radio.Button>
          </Radio.Group>
        </FormItem>
        <FormItem
          name='rangeTime'
          rules={[{ required: true }]}
          initialData={[dayjs().subtract(2, 'hour'), dayjs()]}
        >
          <DateRangePicker
            placeholder='请输入查询时间'
            presets={presets}
            enableTimePicker
            onChange={() => form.reset({ type: 'empty', fields: ['rangeTimeType'] })}
            clearable
          />
        </FormItem>

        <FormItem name='messageId'>
          <BuriedWrapper eventId='MESSAGE_TRACE_DETAIL_SEARCH_FOCUS' triggerEvent='onFocus'>
            <Input placeholder='请输入消息ID' clearable />
          </BuriedWrapper>
        </FormItem>
      </>
    ),
    searchExtra: (
      <>
        <FormItem label='消息Key' name='messageKey'>
          <Input placeholder='请输入消息Key' clearable />
        </FormItem>
        <FormItem name='topic' label='Topic'>
          <AutoComplete
            style={{ width: '100%' }}
            defaultValue='persistent://public/default/test-1'
            popupProps={{ overlayInnerStyle: { width: 'auto' } }}
            options={topicOptions}
            placeholder='请输入Topic'
            clearable
          />
        </FormItem>
        <FormItem name='limit' label='查询条数' initialData='100'>
          <InputNumber
            theme='column'
            placeholder='请输入查询条数'
            clearable
            style={{ width: 200 }}
          />
        </FormItem>
      </>
    ),
  });

  useEffect(() => {
    if (clusterName) {
      loadOptions();
    }
  }, [loadOptions, clusterName]);

  useEffect(() => {
    if (clusterName) {
      form.reset();
      doSearch(null, true);
    }
  }, [clusterName, doSearch, form]);

  return (
    <Root>
      {header}
      <Table rowKey='key' {...config} />
    </Root>
  );
}

export default MessageList;

const Root = styled(ShadowCard)`
  margin-right: 20px;
  .t-form-inline .t-form__item {
    margin-right: var(--td-comp-margin-s);
  }
`;

// const Tag = styled.div`
//   background: rgba(102, 136, 255, 0.12);
//   border-radius: 6px;
//   padding: 2px 10px;
//   font-size: 12px;
//   color: #6688ff;
//   display: flex;
//   align-items: center;
//   justify-content: center;
// `;
